import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import { getPopularMovies, searchMovies, filterMovies, type EraKey } from "@/utils/movie";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [input, setInput] = useState(q);
  const [region, setRegion] = useState("全部");
  const [era, setEra] = useState<EraKey>("all");

  useDocumentTitle(
    q ? `搜索「${q}」 - 三级片电影` : "影片搜索 - 三级片电影",
    "在三级片电影中按影片名称或剧情关键词搜索你感兴趣的三级影片。"
  );

  useEffect(() => {
    setInput(q);
    setRegion("全部");
    setEra("all");
  }, [q]);

  const results = useMemo(
    () => filterMovies(searchMovies(q), { region, era, sort: "latest" }),
    [q, region, era]
  );

  const popular = useMemo(() => getPopularMovies(6), []);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = input.trim();
    setParams(next ? { q: next } : {});
  };

  const resetFilters = () => {
    setRegion("全部");
    setEra("all");
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影片搜索" }]} />

        <header className="mt-4">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">影片搜索</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            输入影片名称或剧情关键词，快速找到想看的三级影片。
          </p>
        </header>

        <form onSubmit={handleSearch} className="mt-5 flex flex-col sm:flex-row items-stretch gap-2 max-w-3xl">
          <div className="relative flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line" />
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="如：都市、悬疑、红颜劫"
              className="w-full rounded-md border border-background-200/70 bg-background-100 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="shrink-0 rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-foreground-950 hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        {q && (
          <div className="mt-6 flex flex-col gap-4">
            <p className="text-sm text-foreground-600">
              关键词 <span className="text-accent-500 font-medium">“{q}”</span> 的结果：
              <span className="text-foreground-950 font-medium"> {results.length} </span> 部影片
            </p>
            <FilterBar region={region} onRegionChange={setRegion} era={era} onEraChange={setEra} />
          </div>
        )}

        {q && results.length > 0 && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}

        {q && results.length === 0 && (
          <div className="mt-8 flex flex-col items-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500 mb-4">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
              没有找到与“{q}”相关的影片
            </h2>
            <p className="text-sm text-foreground-500 mb-6">试试更换关键词，或调整地区与年代筛选条件。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950 hover:bg-accent-600 transition-colors"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}

        {!q && (
          <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-fire-line text-accent-500" />
              <h2 className="font-heading text-lg font-semibold text-foreground-950">热门影片推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {popular.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="h-12 md:h-16" />
    </SiteLayout>
  );
}